import React, { FormEvent, useContext, useState, JSX } from 'react';
import { WorkflowContext } from "../../context/WorkflowContextProvider";
import Form from '@rjsf/mui';
import { IChangeEvent } from '@rjsf/core';
import { RegistryWidgetsType, RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { WorkflowNode, ToolOption, Workflow, Agent } from "../../../../types/workflow";
import { useReactFlow, applyNodeChanges, Node, ReactFlowInstance } from "@xyflow/react";
import { Tool } from "../../../../types/entities";
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { processNodeFormSubmit, processNodeDelete, nodeDescription } from "./functions/workflow-node-create-edit";
import { PromptTemplateFormWidget } from "./components/PromptTemplateFormWidget";
import { LLMModelWidget } from "./components/LLMModelWidget";
import { DefaultFormDialog  } from "./components/DefaultFormDialog";
import { NodeDataStoreSelectWidget } from "./components/NodeDataStoreSelectWidget";
import { ModelSchemaWidget } from "./components/ModelSchemaWidget";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
    Button
} from '@mui/material';

export const WorkflowNodeCreateEdit: () => JSX.Element = (): JSX.Element => {
    const context: any = useContext(WorkflowContext);
    const reactFlowInstance: ReactFlowInstance = useReactFlow();
    const { setEdges } = useReactFlow();

    function handleCloseDialog(): void {
        context.toggleShowModal(false, true);
    }

    const handleFormSubmit: (data: IChangeEvent, event: FormEvent<any>) => void = (data: IChangeEvent, event: FormEvent<any>) : void => {
        // console.log(data.formData);
        const selectedNode: Node = context.selectedNode;
        if (selectedNode !== undefined && selectedNode.data.workflowId === undefined) {
            data.formData.workflowId = undefined;
        }
        processNodeFormSubmit(context, data.formData, reactFlowInstance, setEdges);
        // handleCloseDialog();
    }

    function deleteNode(): void {
        processNodeDelete(context, reactFlowInstance);
        handleCloseDialog();
    }

    function prepareTools(): ToolOption[] {
        let returnTools: ToolOption[] = [];
        for (const tool of context.tools) {
            returnTools.push({
               title: tool.name,
               'const': tool.operatorToolId.toString()
            });
        }
        return returnTools;
    }

    function duplicateNode(): void {
        if (context?.selectedNode?.id) {
            const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
            let result = "";
            for (let i: number = 0; i < 12; i++) {
                result += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            const currentNode: any = reactFlowInstance.getNode(context.selectedNode.id);
            let duplicateNode: any = structuredClone(currentNode);
            duplicateNode.id = result;
            duplicateNode.position.x = duplicateNode.position.x + 200 || 300
            duplicateNode.position.y = duplicateNode.position.x + 200 || 300
            let nodes = reactFlowInstance.getNodes();
            let newNodes: any = [duplicateNode];
            for (const node of nodes) {
                newNodes.push(node);
            }
            context.updateLayoutNodes(newNodes);
        }

    }

    const formSchema: RJSFSchema = {
        title: '',
        type: 'object',
        required: [],
        properties: {
            label: {
                title: 'Name',
                type: 'string',
            },
            agent_type: {
                title: 'Agent Type',
                type: 'string',
                enum: [
                    'Standard',
                    'Supervisor'
                ],
                default: 'Standard',
                description: 'A supervisor agent will manage other agents to complete a task'
            },
            system_prompt: {
                type: 'string',
            },
            action_prompt: {
                title: 'Agent action prompt',
                description: 'Give the Agent a task',
                type: 'string'
            },
            stream_agent_response: {
                title: 'Stream Agent Response',
                type: 'boolean',
                description: 'Allow agent to stream a response to the UI like GPT'
            },
            agent_description_for_tool: {
                type: 'string',
                title: 'Agent Description',
            },
            tools: {
                title: 'Tools',
                type: 'array',
                items: {
                    type: 'string',
                    anyOf: prepareTools()
                },
                uniqueItems: true,
                description: 'Tools the Agent has access to',
            },
            llm: {
                title: 'LLM',
                type: 'string',
                default: ''
            },
            stop_sequences: {
                title: 'Stop Sequences',
                type: 'array',
                items: {
                    type: 'string',
                    title: ' '
                }
            },
            // HQ-52. Offered for every provider: the Operator reads this key as `max_tokens`, which
            // every model settings class declares. The key keeps its old name so saved workflows
            // still work.
            openai_max_completion_tokens: {
                type: 'number',
                title: 'Max Tokens',
                default: 0,
                description: "Caps the length of the response. 0 means no limit"
            },
            expected_output: {
                title: 'Expected Output',
                type: "string",
                enum: ["Text", "Number", "True/False", 'Array', 'Data Model'],
                default: 'Text',
            },
            'settings': {
                title: 'Settings',
                description: 'Settings for the Agent LLM',
                type: 'object',
                properties: {
                    temperature: {
                        title: 'Temperature',
                        type: 'number',
                        default: 0.7,
                    },
                    seed: {
                        title: 'Seed',
                        type: 'number',
                    },
                    exact_model_slug: {
                        title: 'Exact Model Slug',
                        type: 'string',
                        description: 'This is the model date, e.g gpt-5-mini-2025-08-07, this will append the 2025-08-07 to the model.'
                    }
                }
            },
            'data_management': {
                title: 'Data store',
                type: 'string',
                default: 'default',
                description : 'Store the node response in one of 10 slots. Only the default is processed by the workflow. Text responses are added to the message history'
            },
            add_output_to_message: {
                title: 'Add Output To Messages',
                type: 'boolean',
                default: false,
                description: 'Store output as a message to keep in the message history as well as Data store',
            },
            take_screenshot: {
                title: 'Take Screenshot',
                type: 'boolean',
                default: false,
                description: "If the agent returns a URL in the payload and needs to be send a screenshot to the UI"
            }
        },
        allOf: [
            {
                if: {
                    properties: {
                        expected_output: {
                            const: 'Data Model'
                        }
                    }
                },
                then: {
                    properties: {
                        expected_output_format: {
                            title: 'Custom Data Model Structure',
                            type: 'string',
                            description: "Custom JSON to create custom Data Model"
                        },
                    }
                }
            },
            {
                if: {
                    properties: {
                        agent_type: {
                            const: 'Standard'
                        }
                    }
                },
                then: {
                    properties: {
                        system_prompt: {
                            title: 'Agent\'s - System Prompt',
                            description: 'Describe what the agent does, give it some context.'
                        },

                    },
                    required: [
                        'agent_description_for_tool',
                    ]
                }
            },
            {
                if: {
                    properties: {
                        agent_type: {
                            const: 'Supervisor'
                        }
                    }
                },
                then: {
                    properties: {
                        system_prompt: {
                            title: 'Supervisor Agent - System Prompt',
                            description: 'Describe how the agent manages other agents'
                        },
                        tools: {
                            readOnly: true,
                            description: 'Supervisor agents rely on other agents for tools'
                        },
                    }
                }
            },
            {
                if: {
                    properties: {
                        data_management: {
                            const: 'messages'
                        }
                    }
                },
                then: {
                    properties: {
                        add_output_to_message: {
                            readOnly: true,
                        }
                    }
                }
            },
            {
                if: {
                    properties: {
                        llm: {
                            const: "chatgpt-gpt-5",
                        },
                    }
                },
                then: {
                    properties: {
                        openai_send_reasoning_ids: {
                            type: 'boolean',
                            title: 'Open AI Send Reasoning Ids',
                            default: false,
                            description: 'Set to false if streaming agent'
                        },
                        openai_reasoning_effort: {
                            type: 'string',
                            title: 'LLM Reasoning Effort',
                            enum: [
                                'minimal',
                                'low',
                                'medium',
                                'high'
                            ],
                            default: 'minimal',
                            description: 'Minimal or Low for quick LLM responses. Medium and High for correctness over latency.'
                        }
                    }
                }
            },
            {
                if: {
                    properties: {
                        llm: {
                            const: "chatgpt-gpt-5-mini",
                        },
                    }
                },
                then: {
                    properties: {
                        openai_send_reasoning_ids: {
                            type: 'boolean',
                            title: 'Open AI Send Reasoning Ids',
                            default: false,
                            description: 'Set to false if streaming agent'
                        },
                        openai_reasoning_effort: {
                            type: 'string',
                            title: 'LLM Reasoning Effort',
                            enum: [
                                'minimal',
                                'low',
                                'medium',
                                'high'
                            ],
                            default: 'minimal',
                            description: 'Minimal or Low for quick LLM responses. Medium and High for correctness over latency.'
                        }
                    }
                }
            },
            {
                if: {
                    properties: {
                        llm: {
                            const: "chatgpt-gpt-5-nano",
                        },
                    }
                },
                then: {
                    properties: {
                        openai_send_reasoning_ids: {
                            type: 'boolean',
                            title: 'Open AI Send Reasoning Ids',
                            default: false,
                            description: 'Set to false if streaming agent'
                        },
                        openai_reasoning_effort: {
                            type: 'string',
                            title: 'LLM Reasoning Effort',
                            enum: [
                                'minimal',
                                'low',
                                'medium',
                                'high'
                            ],
                            default: 'minimal',
                            description: 'Minimal or Low for quick LLM responses. Medium and High for correctness over latency.'
                        }
                    }
                }
            },
            {
                if: {
                    properties: {
                        take_screenshot: {
                            const: true
                        }
                    }
                },
                then: {
                    properties: {
                        screenshot_url_key: {
                            type: 'string',
                            title: 'URL location for screenshot',
                            default: '',
                            description: 'URL location for screenshot, use value_one/value_two for depth'
                        }
                    }
                }
            }
        ]
    }

    const uiSchema: any = {
        'system_prompt': {
            'ui:widget': 'PromptTemplateFormWidget'
        },
        'action_prompt': {
            'ui:widget': 'PromptTemplateFormWidget'
        },
        expected_output_format: {
            "ui:widget": "ModelSchemaWidget",
        },
        'llm': {
            'ui:widget': 'LLMModelWidget'
        },
        'agent_description_for_tool': {
            'ui:widget': 'textarea'
        },
        'data_management': {
            'ui:widget': 'NodeDataManagementWidget',
            'ui:options': {
                'classNames': 'data-management',
                enumDisabled: [
                    'hq_params',
                    'data_to_loop',
                    'scraper_pending'
                ]
            }
        },
        'ui:order': [
            'label',
            'agent_type',
            'system_prompt',
            'action_prompt',
            'stream_agent_response',
            'agent_description_for_tool',
            'tools',
            'llm',
            'openai_send_reasoning_ids',
            'openai_max_completion_tokens',
            'openai_reasoning_effort',
            'stop_sequences',
            'expected_output',
            'allOf',
            'expected_output_format',
            'settings',
            'data_management',
            'add_output_to_message',
            'take_screenshot',
            'screenshot_url_key'
        ]
    }

    if (context.selectedNode !== undefined) {
        const node: Node = context.selectedNode;
        const edges = reactFlowInstance.getEdges();
        let thisNodeEdges = [];
        edges.forEach(edge => {
            if (edge.target === node.id && edge.sourceHandle === 'supervisor-source') {
                thisNodeEdges.push(edge);
            }
        });
        if (thisNodeEdges.length > 0) {
            uiSchema.agent_type = {
                'ui:options': {
                    enumDisabled: [
                        'Supervisor',
                    ]
                }
            };
            // uiSchema.expected_output = {
            //     'ui:disabled': true
            // }
            uiSchema.action_prompt = {
                'ui:widget': 'PromptTemplateFormWidget',
                'ui:disabled': true
            }
            uiSchema.data_management = {
                'ui:widget': 'NodeDataManagementWidget',
                'ui:disabled': true
            }
        }
    }

    const widgets: RegistryWidgetsType = {
        PromptTemplateFormWidget: PromptTemplateFormWidget,
        LLMModelWidget: LLMModelWidget,
        NodeDataManagementWidget: NodeDataStoreSelectWidget,
        ModelSchemaWidget: ModelSchemaWidget
    }


    return (
        <DefaultFormDialog
            open={ context.showModal }
            title={ "Agent Node Configuration" }
            handleClose={ handleCloseDialog }
            description={ nodeDescription }
        >
            <span style={ { paddingLeft: '15px', fontWeight: 'bold' } }>{ "Node ID: " + context?.selectedNode?.id }</span>
            <Form
                validator={validator}
                schema={ formSchema }
                uiSchema={ uiSchema }
                widgets={ widgets }
                onSubmit={ handleFormSubmit }
                formData={ (context.selectedNode !== undefined && context.selectedNode.data !== undefined ? context.selectedNode.data :  undefined) }
            >
                <div className="dialog__actions form-group">
                    <Button type="submit"
                            variant="contained"
                            color="success"
                    >
                        <SaveIcon />
                        <span>{ "Save" }</span>
                    </Button>
                    { (context?.selectedNode?.data?.node_id !== undefined ) &&
                        <Button
                            onClick={ duplicateNode }
                            variant="contained"
                            color="warning"
                            type="button">
                            <ContentCopyIcon />
                            <span>{ "Duplicate" }</span>
                        </Button>
                    }
                    { (context.selectedNode !== undefined) &&
                        <Button
                            variant="contained"
                            color="error"
                            onClick={ deleteNode }
                            type="button">
                            <DeleteIcon />
                            <span>{ "Delete" }</span>
                        </Button>
                    }

                </div>
            </Form>
        </DefaultFormDialog>
    );

}